import { Good, GoodGenerationContext } from "@/entities/good";
import { categoryStringForApi, sanitizeCharacteristics } from "@/lib/goodsApiPayload";
import { marketplaceKeyToApi } from "@/marketplaces/marketplaceApiMapping";
import { geniFetch } from "./geniBackend";

export const saveGood = async (good: Good) => {
  const category = categoryStringForApi(good.category);
  const goodObject = {
    marketplace: marketplaceKeyToApi(good.marketplace),
    id: good.id,
    contentType: good.contentType,
    characteristics: sanitizeCharacteristics(good.characteristics),
    competitors: good.competitors,
    images: good.images,
    ...(category !== undefined ? { category } : {}),
    brand: good.brand
  }

  const response = await geniFetch<{ id: string }>("/goods", {
    method: "POST",
    body: JSON.stringify({
      title: good.versions?.[0]?.title || "Товар из CardGenius",
      ...goodObject,
      metadata: {
        publishStatus: good.publishStatus,
      },
    }),
  });

  return response.id as unknown as number;
};

/** Синхронизирует поле `images` товара с актуальной галереей версии. */
export async function patchGoodImages(goodId: string, images: string[]): Promise<void> {
  await geniFetch(`/goods/${goodId}`, {
    method: "PATCH",
    body: JSON.stringify({ images }),
  });
}

/** Подравнивает черновик товара под тип контента мастера (фото / карточка / видео). */
export async function patchDraftGoodMetadata(
  goodId: string,
  patch: {
    contentType?: "card" | "images" | "video";
    marketplace?: ReturnType<typeof marketplaceKeyToApi>;
    title?: string;
    images?: string[];
  }
): Promise<void> {
  const body: Record<string, unknown> = {};
  if (patch.contentType) body.contentType = patch.contentType;
  if (patch.marketplace) body.marketplace = patch.marketplace;
  if (patch.title?.trim()) body.title = patch.title.trim();
  if (patch.images?.length) body.images = patch.images;
  if (Object.keys(body).length === 0) return;
  await geniFetch(`/goods/${goodId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * Записывает/снимает маркер «контент генерируется» в `good.metadata`. Бэкенд при PATCH
 * делает shallow-merge metadata, поэтому здесь безопасно трогать только эти ключи —
 * соседние (`recommendations`, `wb`, `latestVersion`) не затираются. Для очистки
 * передавайте явный `null` в id-полях (см. `generating: false`).
 */
export async function patchGoodGenerationMarker(
  goodId: string,
  marker: {
    generating: boolean;
    contentJobId?: string | null;
    contentJobOwnerId?: string | null;
    originalTitle?: string;
    generationContext?: GoodGenerationContext | null;
  },
): Promise<void> {
  const metadata: Record<string, unknown> = { generating: marker.generating };
  if (marker.contentJobId !== undefined) metadata.contentJobId = marker.contentJobId;
  if (marker.contentJobOwnerId !== undefined) metadata.contentJobOwnerId = marker.contentJobOwnerId;
  if (marker.generationContext !== undefined) metadata.generationContext = marker.generationContext;

  const body: Record<string, unknown> = { metadata };
  if (marker.originalTitle?.trim()) body.title = marker.originalTitle.trim();

  await geniFetch(`/goods/${goodId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
